import { useLayoutEffect, useRef, useState, type PointerEvent, type RefObject } from 'react'
import { useTranslation } from 'react-i18next'
import type { EditSpecDict } from '../../../../generated/nodeDataTypes'
import { textTrackItems, type TextPatch, type TextTrackItem } from './textTrack'

export function useTextPreviewDrag({ root, editSpec, revision, disabled, onDraft, onCommit, onSelect, onGestureStart }: {
  root: RefObject<HTMLDivElement>; editSpec: Pick<EditSpecDict, 'overlays' | 'captions' | 'format'>; revision: number; disabled: boolean
  onDraft?: (key: string, patch: TextPatch | null, revision: number) => void
  onCommit?: (key: string, patch: TextPatch | null, revision: number) => void
  onSelect?: (key: string) => void
  onGestureStart?: () => void
}) {
  const drag = useRef<{ item: TextTrackItem; revision: number; x: number; y: number; width: number; height: number; textWidth: number; mode: 'move' | 'resize' | 'rotate'; patch: TextPatch } | null>(null)
  function begin(event: PointerEvent<HTMLElement>, key: string, mode: 'move' | 'resize' | 'rotate' = 'move') {
    if (disabled || !root.current || event.button !== 0) return
    const item = textTrackItems(editSpec).find((item) => item.key === key)
    if (!item) return
    event.preventDefault()
    event.stopPropagation()
    onGestureStart?.()
    // Inspector fields commit on blur. Pointer capture must not unmount a draft first.
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur()
    onSelect?.(key)
    const bounds = root.current.getBoundingClientRect()
    const scale = Math.min(bounds.width / editSpec.format.width, bounds.height / editSpec.format.height)
    const element = Array.from(root.current.querySelectorAll<HTMLElement>('[data-dw-text-key]')).find((el) => el.dataset.dwTextKey === key)
    const textWidth = element ? element.offsetWidth / editSpec.format.width * 100 : 84
    root.current.setPointerCapture(event.pointerId)
    drag.current = { item, revision, x: event.clientX, y: event.clientY, width: editSpec.format.width * scale, height: editSpec.format.height * scale, textWidth, mode, patch: {} }
  }
  const round = (n: number) => Math.round(n * 10) / 10
  const percent = (n: number) => round(Math.max(0, Math.min(100, n)))
  function move(event: PointerEvent<HTMLDivElement>) {
    const state = drag.current
    if (!state || disabled) return
    const dx = event.clientX - state.x, dy = event.clientY - state.y
    const item = state.item
    if (state.mode === 'move') state.patch = { x: percent((item.x ?? 50) + dx / state.width * 100), y: percent((item.y ?? 84) + dy / state.height * 100) }
    else if (state.mode === 'resize') state.patch = { width_pct: Math.max(1, percent((item.width_pct ?? state.textWidth) + dx / state.width * 200)) }
    else state.patch = { rotation: round(Math.max(-360, Math.min(360, (item.rotation ?? 0) + dx / 2))) }
    onDraft?.(item.key, state.patch, state.revision)
  }
  function finish(cancel = false) {
    const state = drag.current
    drag.current = null
    if (!state) return
    onDraft?.(state.item.key, null, state.revision)
    onCommit?.(state.item.key, !cancel && !disabled && Object.keys(state.patch).length ? state.patch : null, state.revision)
  }
  return { begin, handlers: {
    onPointerDown: (event: PointerEvent<HTMLDivElement>) => {
      if (!(event.target instanceof Element)) return
      const key = event.target.closest<HTMLElement>('[data-dw-text-key]')?.dataset.dwTextKey
      if (key) begin(event, key)
    },
    onPointerMove: move,
    onPointerUp: () => finish(),
    onPointerCancel: () => finish(true),
    onLostPointerCapture: () => finish(true),
  } }
}

export function TextPreviewControls({ root, selectedKey, disabled, editSpec, playhead, onBegin }: {
  root: RefObject<HTMLDivElement>; selectedKey: string | null; disabled: boolean; editSpec: Pick<EditSpecDict, 'overlays' | 'captions' | 'format'>; playhead: number
  onBegin: (event: PointerEvent<HTMLElement>, key: string, mode: 'move' | 'resize' | 'rotate') => void
}) {
  const { t } = useTranslation('canvas')
  const [box, setBox] = useState<{ left: number; top: number; width: number; height: number } | null>(null)
  useLayoutEffect(() => {
    let frame = 0
    function measure() {
      const parent = root.current
      const target = parent && Array.from(parent.querySelectorAll<HTMLElement>('[data-dw-text-key]')).find((el) => el.dataset.dwTextKey === selectedKey)
      const rect = target?.getBoundingClientRect(), outer = parent?.getBoundingClientRect()
      const next = rect && outer && rect.width > 0 ? { left: rect.left - outer.left, top: rect.top - outer.top, width: rect.width, height: rect.height } : null
      setBox((current) => JSON.stringify(current) === JSON.stringify(next) ? current : next)
      frame = requestAnimationFrame(measure)
    }
    if (selectedKey && !disabled) measure()
    else setBox(null)
    return () => cancelAnimationFrame(frame)
  }, [root, selectedKey, disabled, editSpec, playhead])
  if (!box || !selectedKey || disabled) return null
  return <div data-testid="text-preview-selection" className="pointer-events-none absolute z-30 border border-primary" style={box}>
    <button type="button" aria-label={t('textTrack.move')} className="pointer-events-auto absolute inset-0 cursor-move bg-transparent focus-visible:ring-2 focus-visible:ring-primary" onPointerDown={(event) => onBegin(event, selectedKey, 'move')} />
    {['left-0 top-0', 'right-0 top-0', 'left-0 bottom-0', 'right-0 bottom-0'].map((position) => <span key={position} className={`absolute h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-primary bg-foreground ${position}`} />)}
    <button type="button" aria-label={t('textTrack.resize')} className="pointer-events-auto absolute -right-2 top-1/2 h-4 w-3 -translate-y-1/2 cursor-ew-resize rounded border border-primary bg-foreground focus-visible:ring-2 focus-visible:ring-primary" onPointerDown={(event) => onBegin(event, selectedKey, 'resize')} />
    <button type="button" aria-label={t('textTrack.rotate')} className="pointer-events-auto absolute -top-7 left-1/2 h-4 w-4 -translate-x-1/2 cursor-ew-resize rounded-full border border-primary bg-foreground focus-visible:ring-2 focus-visible:ring-primary" onPointerDown={(event) => onBegin(event, selectedKey, 'rotate')} />
  </div>
}
